import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { forceCleanLogout, isAuthDeadError } from '@/lib/authRecovery';

// ============================================
// 🚀 Auth + Profile centralizados
// IMPORTANTE: NÃO faz getSession nem registra onAuthStateChange aqui.
// Reutiliza a session já cacheada pelo UserProfileProvider via React Query
// (queryKey 'auth-session'). Antes, cada um dos ~100 componentes que
// chamava useAuth() criava um listener próprio e um setTimeout de 10s,
// saturando o event-loop quando o refresh token estava inválido.
// ============================================

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isLoadingProfile: boolean; // Loading completo (auth + profile)
  profile: any | null; // Vem do contexto centralizado
}

export const useAuth = (): AuthState & {
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
} => {
  // 🚀 Perfil vem do contexto centralizado
  const { profile, isLoadingProfile } = useUserProfileContext();

  // 🚀 Reaproveita a query de session já criada por UserProfileProvider.
  // Como o queryKey é o mesmo, NÃO dispara nova requisição — só lê do cache.
  // Se ainda não houver provider montado (ex: páginas públicas), o React Query
  // executará 1x e demais hooks compartilham o mesmo resultado.
  const { data: session, isLoading: authLoading } = useQuery({
    queryKey: ['auth-session'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      // Se refresh token quebrou, força logout limpo (limpa storage +
      // redireciona pra /auth com mensagem amigável). Não usa signOut()
      // porque essa função PODE travar quando o refresh está em loop.
      if (error && isAuthDeadError(error)) {
        forceCleanLogout('getSession_error');
        return null;
      }
      return data.session ?? null;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const user = session?.user ?? null;

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Erro no logout:', error.message);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return {
    user,
    session: session ?? null,
    loading: authLoading, // APENAS auth, não perfil
    isLoadingProfile, // Loading completo (auth + profile)
    profile, // Do contexto centralizado
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword
  };
};
